import type { SongEntry, StorageReport } from '../library/index.js';
import { ResponsiveSettings } from './ResponsiveSettings.js';
import { DifficultyRow, WaveSpeedRow, type DifficultyChoice } from './Selectors.js';

/**
 * Song Select — the screen the game opens on.
 *
 * It replaces the start gate rather than sitting beside it. The gate existed to
 * provide the one user gesture a browser demands before audio may start; PLAY
 * here is that gesture, and having two screens that both offer a difficulty
 * picker would mean two answers to "can this be changed now".
 *
 * The pickers are the same objects the gate used, moved into `Selectors.ts`.
 * This screen draws a list and a detail panel around them and decides nothing:
 * which difficulties a song has, whether a switch is allowed, what a wave speed
 * does, and whether a song can be deleted are all answered above it.
 *
 * Covers are fetched for the **selected** song only. A library of fifty songs
 * must not decode fifty images to draw a list.
 */

const STYLE_ID = 'mandalizm-select-style';

const CSS = `
.mandalizm-select {
  position: fixed; inset: 0; z-index: 50; display: flex; flex-direction: column;
  padding: 26px clamp(16px, 4vw, 54px) 18px;
  box-sizing: border-box;
  background: radial-gradient(circle at 50% 40%, rgba(2,20,26,0.82), rgba(0,2,8,0.96));
  font: 13px/1.7 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; color: #bff4ee;
  transition: opacity 380ms ease;
}
.mandalizm-select[hidden] { display: none; }
.mandalizm-select--leaving { opacity: 0; pointer-events: none; }
.mandalizm-select__top { display: flex; justify-content: space-between; align-items: baseline; padding-left:32px; }
.mandalizm-select__title {
  font-size: 22px; letter-spacing: 0.22em; color: #eafeff;
  text-shadow: 0 0 22px rgba(0, 220, 200, 0.35);
}
.mandalizm-select__storage { font-size: 11px; color: rgba(140, 205, 220, 0.62); text-align: right; }
.mandalizm-select__body {
  flex: 1 1 auto; min-height: 0; display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(280px, 1.2fr);
  gap: clamp(16px, 3vw, 40px); margin-top: 18px;
}
@media (max-width: 760px) {
  .mandalizm-select__body { grid-template-columns: 1fr; grid-template-rows: minmax(120px, 1fr) auto; }
}
.mandalizm-select__list { overflow-y: auto; padding-right: 6px; }
.mandalizm-select__empty { color: rgba(150, 215, 225, 0.65); padding: 18px 4px; }
.mandalizm-select__song {
  display: block; width: 100%; text-align: left; appearance: none; cursor: pointer;
  font: inherit; margin-bottom: 8px; padding: 11px 13px; color: #b6e8e4;
  background: rgba(3, 30, 38, 0.7); border: 1px solid rgba(90, 240, 220, 0.18);
  border-radius: 7px; transition: background 140ms ease, border-color 140ms ease;
}
.mandalizm-select__song:hover:not(:disabled) { background: rgba(6, 62, 70, 0.9); }
.mandalizm-select__song[aria-pressed="true"] {
  color: #eafeff; background: rgba(8, 96, 104, 0.92); border-color: rgba(120, 250, 232, 0.7);
}
.mandalizm-select__song:disabled { opacity: 0.45; cursor: not-allowed; }
.mandalizm-select__song b { display: block; font-weight: 600; font-size: 14px; letter-spacing: 0.04em; }
.mandalizm-select__song small {
  display: block; margin-top: 3px; font-size: 11px; color: rgba(150, 215, 225, 0.7);
}
.mandalizm-select__detail { overflow-y: auto; padding-right: 4px; }
.mandalizm-select__cover {
  width: 128px; height: 128px; border-radius: 8px; object-fit: cover;
  background: rgba(3, 26, 34, 0.85); border: 1px solid rgba(90, 240, 220, 0.2);
  display: grid; place-items: center; font-size: 10px; letter-spacing: 0.18em;
  color: rgba(140, 205, 220, 0.5); margin-bottom: 14px; overflow: hidden;
}
.mandalizm-select__cover canvas { width: 100%; height: 100%; display: block; }
.mandalizm-select__name { font-size: 18px; color: #eafeff; word-break: break-word; }
.mandalizm-select__author { color: #7fe9dc; margin-bottom: 10px; }
.mandalizm-select__facts {
  display: flex; gap: 18px; flex-wrap: wrap; margin-bottom: 16px;
  font-size: 11px; color: rgba(150, 215, 225, 0.75);
}
.mandalizm-select__facts b { display: block; color: #eafeff; font-size: 13px; font-weight: 500; }
.mandalizm-select__play {
  appearance: none; cursor: pointer; font: inherit; letter-spacing: 0.2em; width: 100%;
  padding: 14px 20px; color: #eafeff; margin-bottom: 10px;
  background: rgba(4, 60, 66, 0.9); border: 1px solid rgba(90, 240, 220, 0.5);
  border-radius: 7px; transition: background 160ms ease;
}
.mandalizm-select__play:hover:not(:disabled) { background: rgba(6, 92, 100, 0.95); }
.mandalizm-select__play:disabled { opacity: 0.4; cursor: progress; }
.mandalizm-select__row { display: flex; gap: 8px; flex-wrap: wrap; }
.mandalizm-select__action {
  appearance: none; cursor: pointer; font: inherit; font-size: 11px; letter-spacing: 0.14em;
  padding: 9px 14px; color: #9fd8dd;
  background: rgba(3, 26, 34, 0.72); border: 1px solid rgba(90, 240, 220, 0.2);
  border-radius: 6px; transition: background 140ms ease, color 140ms ease;
}
.mandalizm-select__action:hover:not(:disabled) { background: rgba(6, 58, 66, 0.9); color: #eafeff; }
.mandalizm-select__action:disabled { opacity: 0.4; cursor: not-allowed; }
.mandalizm-select__action--danger { color: #ff9ab4; border-color: rgba(255, 130, 165, 0.28); }
.mandalizm-select__action--danger:hover:not(:disabled) { background: rgba(70, 10, 26, 0.9); color: #ffd3de; }
.mandalizm-select__bottom {
  display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
  margin-top: 16px; padding-top: 14px; border-top: 1px solid rgba(90, 240, 220, 0.14);
}
.mandalizm-select__status { margin-left: auto; color: rgba(150, 215, 225, 0.75); font-size: 11px; }
.mandalizm-select__status--error { color: #ff86a6; }
.mandalizm-select__confirm {
  position: fixed; inset: 0; z-index: 80; display: grid; place-items: center;
  background: rgba(0, 4, 8, 0.82);
}
.mandalizm-select__confirm[hidden] { display: none; }
.mandalizm-select__confirmBox {
  width: min(420px, 86vw); padding: 22px; border-radius: 9px;
  background: rgba(0, 12, 18, 0.97); border: 1px solid rgba(90, 240, 220, 0.3);
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.6);
}
.mandalizm-select__confirmTitle { color: #eafeff; font-size: 15px; margin-bottom: 10px; }
.mandalizm-select__confirmBody {
  color: rgba(150, 215, 225, 0.8); font-size: 12px; margin-bottom: 18px; white-space: pre-wrap;
}
`;

export interface SongSelectHandlers {
  readonly onTitle?: () => void;
  readonly onSelect: (packId: string) => void | Promise<void>;
  readonly onPlay: (packId: string) => void | Promise<void>;
  readonly onDifficulty: (value: string) => void | Promise<void>;
  readonly onWaveSpeed: (value: number) => void | Promise<void>;
  readonly onCreate: () => void;
  readonly onImport: (file: File) => void | Promise<void>;
  readonly onExport: (packId: string) => void | Promise<void>;
  readonly onDelete: (packId: string) => void | Promise<void>;
  readonly onSettings: () => void;
}

export class SongSelectScreen {
  private readonly advancedSettings: ResponsiveSettings;
  readonly element: HTMLDivElement;
  readonly difficulties = new DifficultyRow();
  readonly waveSpeeds = new WaveSpeedRow();

  private readonly handlers: SongSelectHandlers;
  private readonly list: HTMLDivElement;
  private readonly cover: HTMLDivElement;
  private readonly name: HTMLDivElement;
  private readonly author: HTMLDivElement;
  private readonly facts: HTMLDivElement;
  private readonly playButton: HTMLButtonElement;
  private readonly exportButton: HTMLButtonElement;
  private readonly deleteButton: HTMLButtonElement;
  private readonly importInput: HTMLInputElement;
  private readonly status: HTMLDivElement;
  private readonly storage: HTMLDivElement;
  private readonly bottomButtons: HTMLButtonElement[] = [];
  private readonly uploadButtons: HTMLButtonElement[] = [];
  private readonly songButtons = new Map<string, HTMLButtonElement>();
  private readonly confirm: HTMLDivElement;
  private readonly confirmTitle: HTMLDivElement;
  private readonly confirmBody: HTMLDivElement;
  private readonly confirmActions: HTMLDivElement;
  private selectedPackId: string | null = null;

  constructor(handlers: SongSelectHandlers, parent: HTMLElement = document.body) {
    injectStyle();
    this.handlers = handlers;

    this.element = document.createElement('div');
    this.element.className = 'mandalizm-select';

    const top = document.createElement('div');
    top.className = 'mandalizm-select__top';
    const title = document.createElement('div');
    title.className = 'mandalizm-select__title';
    title.textContent = 'MANDALIZM';
    this.storage = document.createElement('div');
    this.storage.className = 'mandalizm-select__storage';
    top.append(title, this.storage);

    const body = document.createElement('div');
    body.className = 'mandalizm-select__body';

    this.list = document.createElement('div');
    this.list.className = 'mandalizm-select__list';
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('aria-label', 'Song library');

    const detail = document.createElement('div');
    detail.className = 'mandalizm-select__detail';
    this.cover = document.createElement('div');
    this.cover.className = 'mandalizm-select__cover';
    this.cover.textContent = 'NO COVER';
    this.name = document.createElement('div');
    this.name.className = 'mandalizm-select__name';
    this.author = document.createElement('div');
    this.author.className = 'mandalizm-select__author';
    this.facts = document.createElement('div');
    this.facts.className = 'mandalizm-select__facts';

    this.playButton = document.createElement('button');
    this.playButton.className = 'mandalizm-select__play';
    this.playButton.type = 'button';
    this.playButton.textContent = 'PLAY';
    this.playButton.disabled = true;
    this.playButton.addEventListener('click', () => {
      if (this.selectedPackId === null) return;
      void handlers.onPlay(this.selectedPackId);
    });

    const songActions = document.createElement('div');
    songActions.className = 'mandalizm-select__row';
    this.exportButton = action('EXPORT SONGPACK');
    this.exportButton.addEventListener('click', () => {
      if (this.selectedPackId !== null) void handlers.onExport(this.selectedPackId);
    });
    this.deleteButton = action('DELETE SONG', true);
    this.deleteButton.addEventListener('click', () => {
      if (this.selectedPackId !== null) void handlers.onDelete(this.selectedPackId);
    });
    songActions.append(this.exportButton, this.deleteButton);

    detail.append(
      this.cover, this.name, this.author, this.facts,
      this.difficulties.element, this.waveSpeeds.element,
      this.playButton, songActions,
    );
    body.append(this.list, detail);

    const bottom = document.createElement('div');
    bottom.className = 'mandalizm-select__bottom';
    const create = action('CREATE SONG');
    create.addEventListener('click', () => handlers.onCreate());
    const importButton = action('IMPORT SONGPACK');
    this.importInput = document.createElement('input');
    this.importInput.type = 'file';
    this.importInput.accept = '.zip,application/zip';
    this.importInput.hidden = true;
    importButton.addEventListener('click', () => this.importInput.click());
    this.importInput.addEventListener('change', () => {
      const file = this.importInput.files?.[0];
      // Cleared straight away, so choosing the same file twice fires again.
      this.importInput.value = '';
      if (file !== undefined) void handlers.onImport(file);
    });
    const settings = action('SETTINGS');
    settings.addEventListener('click', () => handlers.onSettings());
    this.bottomButtons.push(create, importButton, settings);
    this.uploadButtons.push(create, importButton);

    this.status = document.createElement('div');
    this.status.className = 'mandalizm-select__status';
    bottom.append(create, importButton, settings, this.importInput, this.status);

    this.confirm = document.createElement('div');
    this.confirm.className = 'mandalizm-select__confirm';
    this.confirm.hidden = true;
    const box = document.createElement('div');
    box.className = 'mandalizm-select__confirmBox';
    this.confirmTitle = document.createElement('div');
    this.confirmTitle.className = 'mandalizm-select__confirmTitle';
    this.confirmBody = document.createElement('div');
    this.confirmBody.className = 'mandalizm-select__confirmBody';
    this.confirmActions = document.createElement('div');
    this.confirmActions.className = 'mandalizm-select__row';
    box.append(this.confirmTitle, this.confirmBody, this.confirmActions);
    this.confirm.appendChild(box);

    this.element.append(top, body, bottom, this.confirm);
    this.advancedSettings = new ResponsiveSettings(
      [this.cover, this.author, this.facts, this.waveSpeeds.element, songActions], detail,
      undefined, handlers.onTitle,
    );
    parent.appendChild(this.element);
  }

  get visible(): boolean {
    return !this.element.hidden;
  }

  get selected(): string | null {
    return this.selectedPackId;
  }

  show(): void {
    this.element.hidden = false;
    this.element.classList.remove('mandalizm-select--leaving');
  }

  hide(): void {
    this.advancedSettings.close();
    this.element.classList.add('mandalizm-select--leaving');
    window.setTimeout(() => {
      // Re-checked: the player may have come back before the fade finished.
      if (this.element.classList.contains('mandalizm-select--leaving')) {
        this.element.hidden = true;
      }
    }, 400);
  }

  /** Draws the library. Selection is preserved when the song is still there. */
  setSongs(entries: readonly SongEntry[], selectedPackId: string | null): void {
    this.songButtons.clear();
    this.list.replaceChildren();

    if (entries.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'mandalizm-select__empty';
      empty.textContent = 'No songs yet. CREATE SONG from an audio file, or IMPORT SONGPACK.';
      this.list.appendChild(empty);
      this.setSelected(null);
      return;
    }

    for (const entry of entries) {
      const button = document.createElement('button');
      button.className = 'mandalizm-select__song';
      button.type = 'button';
      button.setAttribute('role', 'option');
      const name = document.createElement('b');
      name.textContent = entry.displayName;
      const meta = document.createElement('small');
      meta.textContent = summarise(entry);
      button.append(name, meta);
      button.addEventListener('click', () => {
        if (entry.packId === this.selectedPackId) return;
        void this.handlers.onSelect(entry.packId);
      });
      this.songButtons.set(entry.packId, button);
      this.list.appendChild(button);
    }

    const stillThere =
      selectedPackId !== null && entries.some((entry) => entry.packId === selectedPackId);
    this.setSelected(stillThere ? selectedPackId : (entries[0]?.packId ?? null));
  }

  /** Marks a song as chosen. Does not load it; that is `onSelect`'s job. */
  setSelected(packId: string | null): void {
    this.selectedPackId = packId;
    for (const [key, button] of this.songButtons) {
      button.setAttribute('aria-pressed', String(key === packId));
    }
    const known = packId !== null;
    this.playButton.disabled = !known;
    this.exportButton.disabled = !known;
    this.deleteButton.disabled = !known;
    this.difficulties.setVisible(known);
    if (!known) {
      this.name.textContent = '';
      this.author.textContent = '';
      this.facts.replaceChildren();
      this.setCover(null);
    }
  }

  /** Fills the detail panel from the index record; no assets are read. */
  setDetail(entry: SongEntry): void {
    this.name.textContent = entry.displayName;
    this.author.textContent = entry.author.length > 0 ? entry.author : '—';
    this.facts.replaceChildren(
      fact('BPM', formatBpm(entry.bpm)),
      fact('DURATION', formatDuration(entry.durationSeconds)),
      fact('MV', mvLabel(entry)),
      fact('SOURCE', entry.source.toUpperCase()),
    );
  }

  setDifficulties(
    choices: readonly DifficultyChoice[],
    current: string,
  ): void {
    this.difficulties.setChoices(choices, current, (value) => this.handlers.onDifficulty(value));
  }

  setDifficulty(value: string): void {
    this.difficulties.setValue(value);
  }

  setWaveSpeeds(presets: readonly number[], current: number): void {
    this.waveSpeeds.setPresets(presets, current, (value) => this.handlers.onWaveSpeed(value));
  }

  setWaveSpeed(value: number): void {
    this.waveSpeeds.setValue(value);
  }

  /** Shows cover art, or the placeholder. Takes ownership of nothing. */
  setCover(image: ImageBitmap | null): void {
    this.cover.replaceChildren();
    if (image === null) {
      this.cover.textContent = 'NO COVER';
      return;
    }
    this.cover.textContent = '';
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    canvas.getContext('2d')?.drawImage(image, 0, 0);
    this.cover.appendChild(canvas);
  }

  setStatus(text: string, isError = false): void {
    this.status.textContent = text;
    this.status.classList.toggle('mandalizm-select__status--error', isError);
  }

  setStorage(report: StorageReport): void {
    const own = formatBytes(report.storedBytes);
    const estimate =
      report.usageBytes === null
        ? ''
        : ` · browser reports ${formatBytes(report.usageBytes)}` +
          (report.quotaBytes === null ? '' : ` of about ${formatBytes(report.quotaBytes)}`);
    this.storage.textContent = `STORAGE ${own} in ${report.songs} song${
      report.songs === 1 ? '' : 's'
    }${estimate}`;
  }

  /** Locks everything while a song loads or a pack is written. */
  setBusy(busy: boolean): void {
    this.playButton.disabled = busy || this.selectedPackId === null;
    this.exportButton.disabled = busy || this.selectedPackId === null;
    this.deleteButton.disabled = busy || this.selectedPackId === null;
    for (const button of this.bottomButtons) button.disabled = busy;
    for (const button of this.songButtons.values()) button.disabled = busy;
    this.difficulties.setDisabled(busy);
    this.waveSpeeds.setDisabled(busy);
  }

  setPlayables(playables: boolean): void {
    for (const control of this.uploadButtons) control.hidden = playables;
    this.exportButton.hidden = playables;
    this.deleteButton.hidden = playables;
  }

  /**
   * Asks before doing something destructive.
   *
   * Its own dialog rather than `window.confirm`, which blocks the whole page —
   * including the render loop and, more importantly, the audio callbacks — for
   * as long as it is open.
   */
  ask(title: string, body: string, confirmLabel: string, danger = false): Promise<boolean> {
    this.confirmTitle.textContent = title;
    this.confirmBody.textContent = body;
    this.confirmActions.replaceChildren();
    return new Promise((resolve) => {
      const close = (answer: boolean): void => {
        this.confirm.hidden = true;
        resolve(answer);
      };
      const cancel = action('CANCEL');
      cancel.addEventListener('click', () => close(false));
      const proceed = action(confirmLabel, danger);
      proceed.addEventListener('click', () => close(true));
      this.confirmActions.append(cancel, proceed);
      this.confirm.hidden = false;
      proceed.focus();
    });
  }

  dispose(): void {
    this.advancedSettings.dispose();
    this.element.remove();
  }
}

// ---------------------------------------------------------------------------

/**
 * How this song's music video will be resolved.
 *
 * It used to read `NO` whenever the pack carried no bundled video, which was a
 * lie for every song that plays one: Mirror Drift's third rule looks for
 * `/video/<displayName>.mp4`, and that is how the original packs have always
 * found their MV. Reading "NO MV" beside a song whose video plays perfectly is
 * how you conclude the MV feature is broken when it is not.
 *
 * These say which *rule* applies, not whether the file is there. Answering that
 * would mean a request per song, and a label that lies in the other direction —
 * claiming a file exists — would be worse than one that says where to look.
 */
function mvLabel(entry: SongEntry): string {
  if (entry.hasVideo) return 'IN PACK';
  if (entry.mvReference !== null) return 'LINKED';
  return 'BY TITLE';
}

function summarise(entry: SongEntry): string {
  const parts = [entry.author.length > 0 ? entry.author : '—', `BPM ${formatBpm(entry.bpm)}`];
  parts.push(
    `E${entry.difficulties.easy}/N${entry.difficulties.normal}/H${entry.difficulties.hard}`,
  );
  if (entry.hasVideo) parts.push('MV');
  if (entry.hasCover) parts.push('COVER');
  return parts.join(' · ');
}

function fact(label: string, value: string): HTMLDivElement {
  const wrapper = document.createElement('div');
  const strong = document.createElement('b');
  strong.textContent = value;
  const caption = document.createTextNode(label);
  wrapper.append(strong, caption);
  return wrapper;
}

function action(text: string, danger = false): HTMLButtonElement {
  const button = document.createElement('button');
  button.className = danger
    ? 'mandalizm-select__action mandalizm-select__action--danger'
    : 'mandalizm-select__action';
  button.type = 'button';
  button.textContent = text;
  return button;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export function formatBpm(bpm: number): string {
  if (!Number.isFinite(bpm) || bpm <= 0) return '—';
  return Number.isInteger(bpm) ? String(bpm) : bpm.toFixed(1);
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
